import clsx from 'clsx';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import Slider from 'react-slick';

import { ICONS } from '../../icons';
import Icon from '../../icons/Icon';

import './CommonCarousel.scss';

const CommonCarousel = forwardRef(function CommonCarousel(
  {
    children,
    isLazyLoad,
    onBeforeChange = null,
    initialSlide = 0,
    swipe = true,
    draggable = true,
    extraSettings = {},
    contrastedArrows = false,
    enableArrowNavigation = false,
  },
  ref,
) {
  const wrapperRef = useRef(null);
  const sliderRef = useRef(null);
  const isDisabled = !swipe || !draggable;

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    lazyLoad: isLazyLoad,
    arrows: false,
    pauseOnHover: true,
    adaptiveHeight: true,
    autoplay: false,
    autoplaySpeed: 7000,
    beforeChange: onBeforeChange,
    initialSlide,
    swipe,
    draggable,
    appendDots: (dots) => (
      <div>
        <ul style={{ pointerEvents: isDisabled ? 'none' : 'auto' }}>{dots}</ul>
      </div>
    ),
    customPaging: () => (
      <div className={clsx('common-carousel_dot', isDisabled && 'common-carousel_dot--disabled')}></div>
    ),
    ...extraSettings,
  };

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return;

    const recalc = () => {
      sliderRef.current?.innerSlider?.onWindowResized?.();
    };

    const scheduleRecalc = () => {
      requestAnimationFrame(() => requestAnimationFrame(recalc));
    };

    const onAnyMediaEventCapture = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'IMG' || tag === 'VIDEO') scheduleRecalc();
    };

    root.addEventListener('load', onAnyMediaEventCapture, true);

    root.addEventListener('loadedmetadata', onAnyMediaEventCapture, true);
    root.addEventListener('loadeddata', onAnyMediaEventCapture, true);
    root.addEventListener('canplay', onAnyMediaEventCapture, true);

    return () => {
      root.removeEventListener('load', onAnyMediaEventCapture, true);
      root.removeEventListener('loadedmetadata', onAnyMediaEventCapture, true);
      root.removeEventListener('loadeddata', onAnyMediaEventCapture, true);
      root.removeEventListener('canplay', onAnyMediaEventCapture, true);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    goToSlide: (index) => {
      sliderRef.current?.slickGoTo(index);
    },
    next: () => {
      sliderRef.current?.slickNext();
    },
    prev: () => {
      sliderRef.current?.slickPrev();
    },
  }));

  useEffect(() => {
    if (!enableArrowNavigation) return;

    const handleKeyDown = (e) => {
      if (isDisabled || !sliderRef.current) return;

      if (e.key === 'ArrowRight') {
        sliderRef.current.slickNext();
      } else if (e.key === 'ArrowLeft') {
        sliderRef.current.slickPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDisabled, enableArrowNavigation]);

  return (
    <div className='common-carousel_wrapper' ref={wrapperRef}>
      <Slider ref={sliderRef} {...settings}>
        {children}
      </Slider>

      <button
        type='button'
        disabled={isDisabled}
        className={clsx(
          'common-carousel_button',
          'common-carousel_button--left',
          isDisabled && 'common-carousel_button--disabled',
          contrastedArrows && 'common-carousel_button--contrasted',
        )}
        onClick={() => !isDisabled && sliderRef.current?.slickPrev()}>
        <Icon
          icon={ICONS.CaretUp}
          className={clsx('common-carousel_button-icon', 'common-carousel_button-icon--previous')}
          disableCommonStyles
        />
      </button>
      <button
        type='button'
        disabled={isDisabled}
        className={clsx(
          'common-carousel_button',
          'common-carousel_button--right',
          isDisabled && 'common-carousel_button--disabled',
          contrastedArrows && 'common-carousel_button--contrasted',
        )}
        onClick={() => !isDisabled && sliderRef.current?.slickNext()}>
        <Icon
          icon={ICONS.CaretUp}
          className={clsx('common-carousel_button-icon', 'common-carousel_button-icon--next')}
          disableCommonStyles
        />
      </button>
    </div>
  );
});

export default CommonCarousel;
